import styled from '@emotion/styled'
import { Gray900 } from '@mehilainen/mds-customer/colors'
import React, { useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { SupportedLanguage } from '../../../../__generated__/api'
import { DefaultButton } from '../../../../common/components/DefaultButton/DefaultButton'
import { CenteredRowFlex } from '../../../../common/components/Layout/Layout'
import Loader from '../../../../common/components/Loader/Loader'
import { useApi } from '../../../../common/hooks/useApi'
import api from '../../../../common/services/api'
import { scale } from '../../../../common/utils/scale'
import { stripTagsAllowBasicHtml } from '../../../../common/utils/xss'
import PohjolaSVG from '../../images/Pohjola.svg?react'

import InsuranceVerificationFeedback from './InsuranceVerificationFeedback'
import { ButtonsContainer, ContentContainer, Heading } from './Shared'

// Style for the container that will parse the HTML content from insurance-api
const HtmlContent = styled(CenteredRowFlex)`
  width: 100%;
  flex-direction: column;
  text-align: left;

  h2 {
    font-weight: 500;
    margin-top: ${scale(2)};
    margin-bottom: ${scale(1)};
    text-align: left;
    font-size: 1rem;
  }

  h3 {
    margin-top: ${scale(2)};
    margin-bottom: ${scale(1)};
    text-align: left;
  }

  p {
    margin-bottom: ${scale(1.5)};
    text-align: left;
    font-size: 0.875rem;
    color: ${Gray900};
  }

  ul {
    list-style-position: inside;
    padding-left: 0;
    margin-left: 0;
    margin-bottom: ${scale(1.5)};
    text-align: left;
  }

  li {
    margin-bottom: ${scale(0.5)};
    font-size: 0.875rem;
    color: ${Gray900};
  }

  ul ul {
    list-style-type: circle;
    margin-top: ${scale(0.5)};
    padding-left: ${scale(2)};
  }
`

// Component to parse and render HTML with Text component for insurance content
const renderInsuranceContent = (htmlString: string) => {
  // Make headings focusable by adding tabindex
  const modifiedHtml = htmlString
    .replace(/<h2>/g, '<h2 tabindex="0">')
    .replace(/<h3>/g, '<h3 tabindex="0">')
    .replace(/<li>/g, '<li tabindex="0">')
    .replace(/<p>/g, '<p tabindex="0">')

  return <HtmlContent dangerouslySetInnerHTML={{ __html: stripTagsAllowBasicHtml(modifiedHtml) }} />
}

interface Props {
  translationKey: string
  subjectOmUid: number
  insuranceCompanyId: string
  incidentType: string | undefined
  onSubmit(permitId: string): void
  onBack(): void
  onContinueAsSelfPaying(): void
}

const PohjolaContents: React.FC<React.PropsWithChildren<Props>> = ({
  translationKey,
  subjectOmUid,
  insuranceCompanyId,
  incidentType,
  onSubmit,
  onBack,
  onContinueAsSelfPaying,
}) => {
  const { t, i18n } = useTranslation()
  const titleRef = useRef<HTMLHeadingElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // Set focus to title when component mounts
  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.focus()
    }
  }, [])

  const handleContinueAsSelfPaying = useCallback(() => {
    onContinueAsSelfPaying()
  }, [onContinueAsSelfPaying])

  // Fetch insurance contents
  const {
    data: insuranceContents,
    pending: insuranceContentsPending,
    error: insuranceContentsError,
  } = useApi(
    api.v1.insuranceDirectCompensationPermits,
    {
      subjectOmUid,
      insuranceCompanyId,
      lang: i18n.language as SupportedLanguage,
      incidentType,
    },
    []
  )

  if (insuranceContentsError) {
    throw 'Error fetching insurance contents'
  }

  if (insuranceContentsPending) {
    return (
      <ContentContainer>
        <Loader />
      </ContentContainer>
    )
  }
  if (insuranceContents === null || insuranceContents.length === 0) {
    return (
      <InsuranceVerificationFeedback
        translationKey={translationKey}
        heading={t(`component.${translationKey}.customershipCheck.noCustomership.heading`)}
        text={t(`component.${translationKey}.customershipCheck.noCustomership.text`)}
        onContinueAsSelfPaying={handleContinueAsSelfPaying}
        onBack={onBack}
      />
    )
  }
  const firstInsuranceContent = insuranceContents[0]
  const currentLanguage = i18n.language as SupportedLanguage
  const title = firstInsuranceContent.i18n.title?.[currentLanguage] || ''
  const htmlContent = firstInsuranceContent.i18n.htmlContent?.[currentLanguage] || ''
  return (
    <ContentContainer>
      <PohjolaSVG />
      <Heading ref={titleRef} tabIndex={0}>
        {title}
      </Heading>
      <div ref={contentRef}>{renderInsuranceContent(htmlContent)}</div>
      <ButtonsContainer>
        <DefaultButton onClick={() => onSubmit(firstInsuranceContent.id)}>
          {t(`component.${translationKey}.insuranceContents.continueButton`)}
        </DefaultButton>
        <DefaultButton variant="outlined" onClick={onContinueAsSelfPaying}>
          {t(`component.${translationKey}.customershipCheck.continueAsSelfPaying`)}
        </DefaultButton>
        <DefaultButton variant="text" onClick={onBack}>
          {t('common.goBack')}
        </DefaultButton>
      </ButtonsContainer>
    </ContentContainer>
  )
}

export default PohjolaContents
